import type { PatientSchema } from "@app/types/few-schot-schema";

/** Universal patient-identity keys — mirror of backend ``BASE_IDENTITY_FIELDS``
 *  in ``mage_x_api/utils/schemas.py``. These fields always render in the
 *  patient header card (name, NISS pill, calendar / sexe strip) regardless
 *  of speciality, and are never shown in a body group. The card reads them
 *  directly from the patient object (populated by ``_inject_identity`` from
 *  ``PatientIdentity`` on the backend). */
export const BASE_IDENTITY_FIELD_KEYS = [
  "prenom",
  "nom",
  "num_secu",
  "date_naissance",
  "sexe",
] as const;

export interface PatientFieldGroup {
  name: string;
  emoji?: string;
  sortOrder: number;
  keys: string[];
}

/** Build the body-card sections for the patient modal + page. Identity keys
 *  are excluded — they live in the header card only.
 *
 *  ``hiddenKeys`` carries the practitioner's PERSONAL selection (see
 *  ``zustand/field-visibility``). It is applied here rather than server-side because the
 *  schema is also what the dashboard caches speciality-wide; the fields still arrive in
 *  the payload, so switching one back on needs no refetch. A group whose every key is
 *  hidden disappears rather than rendering as an empty card. */
export function buildPatientFieldGroups(
  schema: PatientSchema,
  hiddenKeys?: ReadonlySet<string>,
): PatientFieldGroup[] {
  const headerOnly = new Set<string>(BASE_IDENTITY_FIELD_KEYS);
  const groups = new Map<string, PatientFieldGroup>();
  for (const [key, def] of Object.entries(schema)) {
    if (headerOnly.has(key)) continue;
    if (hiddenKeys?.has(key)) continue;
    const groupName = def.group ?? "Autres";
    let bucket = groups.get(groupName);
    if (!bucket) {
      bucket = {
        name: groupName,
        emoji: def.group_emoji,
        sortOrder: def.group_sort_order ?? 10_000,
        keys: [],
      };
      groups.set(groupName, bucket);
    }
    if (!bucket.emoji && def.group_emoji) bucket.emoji = def.group_emoji;
    bucket.keys.push(key);
  }
  return Array.from(groups.values()).sort((a, b) => a.sortOrder - b.sortOrder);
}
